/* eslint-disable react/jsx-fragments */
import React, { Component } from "react";
import PropTypes from "prop-types";
import Table from "react-virtualized/dist/es/Table";
import Column from "react-virtualized/dist/es/Table/Column";
import AutoSizer from "react-virtualized/dist/es/AutoSizer";
import InfiniteLoader from "react-virtualized/dist/es/InfiniteLoader";
import defaultRowRenderer from "react-virtualized/dist/es/Table/defaultRowRenderer";
import classnames from "classnames";

import Tooltip from "../tooltip/tooltip";
import SortIndicator from "./sortIndicator";
import LoaderRow from "./loaderRow";
import {
  generateId,
  gatherClasses,
  isString,
  isFunction,
  isObject,
  hasProperty
} from "../../lib/utils";
import Input from "../input/input";
import createDetectElementResize from "../../lib/detectElementResize";
import {
  getMaxHeaderHeight,
  getCellTextAlignmentClass,
  getHeaderContentAlignmentClass,
  getTextAlignmentClass
} from "../../lib/tableUtils";
import SelectionInput from "./selectionInput";

const STATUS_LOADING = 1;
const STATUS_LOADED = 2;

class InfiniteTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadedRowsMap: {},
      scrollToIndex: props.scrollToIndex,
      originalScrollToIndex: undefined,
      headerHeight: props.headerHeight
    };
    this.tableId = generateId("inf-table-");
    this.tableWrpRef = React.createRef();
    this.detectElementResize = null;
    this.isRowLoaded = this.isRowLoaded.bind(this);
    this.loadMoreRows = this.loadMoreRows.bind(this);
    const { loadedRowsMap } = this.state;
    for (let i = 0; i <= props.dataList.length; i++) {
      loadedRowsMap[i] = STATUS_LOADED;
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let newScrollToIndex;
    if (prevState.originalScrollToIndex !== nextProps.scrollToIndex) {
      newScrollToIndex = nextProps.scrollToIndex;
    } else if (isFunction(nextProps.shouldClearLoadMap)) {
      if (nextProps.shouldClearLoadMap() === true) {
        newScrollToIndex = 0;
      } else {
        newScrollToIndex = undefined;
      }
    }

    return {
      ...prevState,
      originalScrollToIndex: nextProps.scrollToIndex,
      scrollToIndex: newScrollToIndex
    };
  }

  cellRenderer = data => {
    const content = this.getCellContent(data);
    const { columnData } = data;

    return (
      <div
        className={gatherClasses(
          null,
          hasProperty(columnData, "truncateText") &&
            columnData.truncateText === false
            ? ""
            : "oui-ellipsis",
          getCellTextAlignmentClass(data, this.props)
        )}
      >
        {content}
      </div>
    );
  };

  isKeyDisabled(key) {
    const { disabledRows } = this.props;
    if (Array.isArray(disabledRows) && disabledRows.includes(key)) {
      return true;
    }
    return false;
  }

  checkboxCellRenderer = cellData => {
    const { tableId, multiSelect, uniqueDataKey, getSelectionInputTooltip } =
      this.props;
    const key = cellData.rowData[uniqueDataKey];
    if (multiSelect === true) {
      return (
        <SelectionInput
          getSelectionInputTooltip={getSelectionInputTooltip}
          rowData={cellData.rowData}
        >
          <Input
            id={`${tableId}-${key}`}
            type="checkbox"
            className="oui-within-table"
            checked={this.isRowSelected(key)}
            onChange={() => {}}
            name="checkbox"
            aria-label="Checkbox select row"
            disabled={this.isKeyDisabled(key)}
          />
        </SelectionInput>
      );
    }
    return (
      <SelectionInput
        getSelectionInputTooltip={getSelectionInputTooltip}
        rowData={cellData.rowData}
      >
        <Input
          id={`${tableId}-${key}`}
          type="radio"
          className="oui-within-table"
          checked={this.isRowSelected(key)}
          name={`radio-group-${this.tableId}`}
          aria-label="Radio select row"
          disabled={this.isKeyDisabled(key)}
        />
      </SelectionInput>
    );
  };

  checkBoxHeaderRenderer = () => {
    const { multiSelect, onSelectAll, selectAll, hideSelectAll } = this.props;
    // No checkbox is required if multiple selection is not allowed
    if (multiSelect === true && !hideSelectAll) {
      return (
        <Input
          id={generateId("select-all-")}
          type="checkbox"
          checked={selectAll}
          onChange={onSelectAll}
          name="selectAllCheckbox"
          aria-label="Checkbox select all"
        />
      );
    }
    return null;
  };

  componentDidMount() {
    const tableParent = this.tableWrpRef.current.parentNode;
    if (tableParent) {
      this.detectElementResize = createDetectElementResize();
      this.detectElementResize.addResizeListener(
        tableParent,
        this.recomputeHeaderHeight
      );
    }
    /* 
            TODO Need to recompute header height after 1 sec, because in the first render the header text remains in single line. And we do not get perfect clientHeight for that element.
            Hence we need to find a way to compute header height post header text elements are fully rendered.
            This is just a temporary hack.
        */
    setTimeout(this.recomputeHeaderHeight, 1000);
  }

  componentDidUpdate(prevProps) {
    const { dataList, shouldClearLoadMap } = this.props;
    if (prevProps.dataList !== dataList) {
      if (shouldClearLoadMap() === true) {
        this.resetTableLoadData();
      } else {
        this.initLoadedState();
      }
    }
  }

  componentWillUnmount() {
    const { hideTooltip } = this.props;
    const tableParent = this.tableWrpRef.current.parentNode;

    if (this.detectElementResize && tableParent) {
      this.detectElementResize.removeResizeListener(
        tableParent,
        this.recomputeHeaderHeight
      );
    }

    if (isFunction(hideTooltip)) {
      hideTooltip();
    }
  }

  recomputeHeaderHeight = () => {
    const { headerHeight: stateHeaderHeight } = this.state;
    const { headerHeight } = this.props;
    const maxHeaderHeight = Math.max(
      getMaxHeaderHeight(this.tableId),
      headerHeight
    );

    if (maxHeaderHeight !== stateHeaderHeight) {
      this.setState({
        headerHeight: maxHeaderHeight
      });
    }
  };

  getCellContent = data => {
    const { columnsDef } = this.props;
    let content = "";
    // Call the caller's cell renderer if defined
    if (columnsDef[data.columnIndex].cellRenderer) {
      content = columnsDef[data.columnIndex].cellRenderer(data);
    } else {
      content = data.cellData;
    }
    return content;
  };

  headerRenderer = ({ dataKey, label, disableSort, columnData }) => {
    const { sortBy, sortDirection, dataList = [] } = this.props;
    const sortingAvailableButDisabled =
      hasProperty(columnData, "sortDisabled") &&
      columnData.sortDisabled === true;

    if (dataList.length === 0 || disableSort) {
      return (
        <div className="oui-header-cell">
          <div
            className={`oui-d-flex ${getHeaderContentAlignmentClass(
              columnData,
              this.props
            )}`}
          >
            <div
              className={`oui-header-text ${getTextAlignmentClass(
                columnData,
                this.props
              )}`}
            >
              {label}
            </div>
            {isFunction(columnData.headerAction) && (
              <div className="oui-header-actions">
                <span className="oui-ml-2">{columnData.headerAction()}</span>
              </div>
            )}
          </div>
          {columnData.secondaryLabel && (
            <div
              className={`oui-header-help-text ${getTextAlignmentClass(
                columnData,
                this.props
              )}`}
            >
              {columnData.secondaryLabel}
            </div>
          )}
        </div>
      );
    }
    return (
      <div
        className="oui-header-cell"
        onClick={sortingAvailableButDisabled ? e => e.stopPropagation() : null}
        aria-hidden="true"
      >
        <div
          className={`oui-d-flex ${getHeaderContentAlignmentClass(
            columnData,
            this.props
          )}`}
        >
          <div
            className={`oui-header-text ${getTextAlignmentClass(
              columnData,
              this.props
            )}`}
          >
            {label}
          </div>
          <div className="oui-header-actions">
            <Tooltip
              text={columnData.sortDisabledTooltip || ""}
              showToolTip={
                sortingAvailableButDisabled &&
                (!!isString(columnData.sortDisabledTooltip) ||
                  !!isFunction(columnData.sortDisabledTooltip))
              }
            >
              <SortIndicator
                sortBy={sortBy}
                sortDirection={sortDirection}
                columnName={dataKey}
                disabled={sortingAvailableButDisabled}
              />
            </Tooltip>
            {isFunction(columnData.headerAction) && (
              <span className="oui-ml-2">{columnData.headerAction()}</span>
            )}
          </div>
        </div>
        {columnData.secondaryLabel && (
          <div
            className={`oui-header-help-text ${getTextAlignmentClass(
              columnData,
              this.props
            )}`}
          >
            {columnData.secondaryLabel}
          </div>
        )}
      </div>
    );
  };

  initLoadedState = () => {
    const { dataList } = this.props;
    const loadedRowsMap = {};

    for (let i = 0; i <= dataList.length; i++) {
      loadedRowsMap[i] = STATUS_LOADED;
    }

    this.setState({
      scrollToIndex: undefined,
      loadedRowsMap
    });
  };

  isRowLoaded({ index }) {
    const { loadedRowsMap } = this.state;
    return !!loadedRowsMap[index]; // STATUS_LOADING or STATUS_LOADED
  }

  isRowSelected = key => {
    const { selectedRows } = this.props;
    if (selectedRows.indexOf(key) >= 0) {
      return true;
    }
    return false;
  };

  isSortEnabled = item => {
    if (item.sortable) {
      return true;
    }
    return false;
  };

  loadMoreRows({ startIndex, stopIndex }) {
    const { loadMoreRows } = this.props;
    const { loadedRowsMap } = this.state;

    for (let i = startIndex; i <= stopIndex; i++) {
      loadedRowsMap[i] = STATUS_LOADING;
    }

    const promise = loadMoreRows(startIndex, stopIndex);

    if (promise) {
      for (let i = startIndex; i <= stopIndex; i++) {
        loadedRowsMap[i] = STATUS_LOADED;
      }
      return promise;
    }
    return undefined;
  }

  noRowsRenderer = () => {
    const { noDataMessage } = this.props;
    return (
      <div className="oui-empty-table-msg oui-text-muted">{noDataMessage}</div>
    );
  };

  onRowClick = e => {
    const { onRowClick, uniqueDataKey } = this.props;
    const key = e.rowData[uniqueDataKey];

    if (isFunction(onRowClick) && !this.isKeyDisabled(key)) {
      onRowClick.call(null, e);
    }
  };

  resetTableLoadData = () => {
    /*  - First scroll to top and then init the loaded state and also revert the scrollToIndex to "undefined",
        so that react-virtualized can handle to scrolling.
        - We need scrollToIndex set to zero because we need to start from top in case of search, sort and filters.
    */
    this.setState(
      {
        scrollToIndex: 0
      },
      this.initLoadedState
    );
  };

  rowRenderer = props => {
    const {
      selectedRows,
      uniqueDataKey,
      updateTooltip,
      hideTooltip,
      getRowStyle
    } = this.props;
    const key = props.rowData[uniqueDataKey];
    const isSelected = selectedRows.indexOf(key) >= 0;
    const isDisabled = this.isKeyDisabled(key);

    props.className = classnames(props.className, {
      "oui-selected": isSelected,
      "oui-row-disabled": isDisabled
    });

    // Tooltip handling
    if (props.isScrolling) {
      if (isFunction(hideTooltip)) {
        hideTooltip();
      }
    } else if (isFunction(updateTooltip)) {
      updateTooltip();
    }

    if (isFunction(getRowStyle)) {
      const rowColor = getRowStyle(props.rowData, isSelected);

      if (isObject(rowColor) && Object.keys(rowColor).length > 0) {
        props.style = { ...(props.style || {}), ...rowColor };
      }
    }

    return defaultRowRenderer(props);
  };

  sort = ({ sortBy, sortDirection }) => {
    const { sort } = this.props;
    sort(sortBy, sortDirection);
  };

  render() {
    const {
      noCheckboxTable,
      dataList,
      columnsDef,
      sortBy,
      sortDirection,
      threshold,
      minimumBatchSize,
      overscanRowCount,
      rowHeight,
      remoteRowCount,
      isLoading,
      alignItems,
      dataTestId,
      isInvalid,
      isRowDisabled,
      footerClassName,
      footerRenderer
    } = this.props;
    const totalRecords = dataList.length;
    const { headerHeight, scrollToIndex } = this.state;

    return (
      <div
        data-testid={dataTestId}
        id={this.tableId}
        ref={this.tableWrpRef}
        className={gatherClasses(
          this.props,
          "oui-inf-loadertbl-wrp",
          isInvalid ? "oui-is-invalid" : null
        )}
      >
        <div className="oui-inf-loadertbl-inr">
          <InfiniteLoader
            threshold={threshold}
            minimumBatchSize={minimumBatchSize}
            isRowLoaded={this.isRowLoaded}
            loadMoreRows={this.loadMoreRows}
            rowCount={remoteRowCount}
          >
            {({ onRowsRendered, registerChild }) => (
              <AutoSizer>
                {({ width, height }) => (
                  <React.Fragment>
                    <Table
                      ref={registerChild}
                      className="oui-react-virtualized-table"
                      headerClassName="oui-react-virtualized-table-header"
                      rowClassName="oui-react-virtualized-table-row"
                      gridClassName="oui-react-virtualized-table-grid"
                      width={width}
                      height={
                        isFunction(footerRenderer)
                          ? isLoading
                            ? height - rowHeight - 40
                            : height - rowHeight
                          : height
                      }
                      headerHeight={headerHeight}
                      rowHeight={rowHeight}
                      rowCount={totalRecords}
                      sort={totalRecords > 0 ? this.sort : null}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      rowGetter={({ index }) => dataList[index]}
                      rowRenderer={this.rowRenderer}
                      onRowsRendered={onRowsRendered}
                      scrollToAlignment="start"
                      scrollToIndex={scrollToIndex}
                      overscanRowCount={overscanRowCount}
                      onRowClick={this.onRowClick}
                      noRowsRenderer={this.noRowsRenderer}
                    >
                      {columnsDef.map((item, index) => {
                        if (index === 0 && !noCheckboxTable) {
                          return (
                            <Column
                              className={gatherClasses(
                                null,
                                "oui-react-virtualized-table-column",
                                `oui-vertical-align-${
                                  item.alignItems || alignItems
                                }`,
                                item.cellClassName
                              )}
                              headerClassName="oui-react-virtualized-table-header-column"
                              style={
                                isFunction(item.getCellStyle)
                                  ? item.getCellStyle()
                                  : {}
                              }
                              columnData={item}
                              key={item.dataKey}
                              dataKey={item.dataKey}
                              width={26}
                              cellRenderer={this.checkboxCellRenderer}
                              disableSort={!this.isSortEnabled(item)}
                              headerRenderer={this.checkBoxHeaderRenderer}
                            />
                          );
                        }
                        isRowDisabled(item);
                        return (
                          <Column
                            className={gatherClasses(
                              null,
                              "oui-react-virtualized-table-column",
                              `oui-vertical-align-${
                                item.alignItems || alignItems
                              }`,
                              item.cellClassName
                            )}
                            headerClassName="oui-react-virtualized-table-header-column"
                            style={
                              isFunction(item.getCellStyle)
                                ? item.getCellStyle()
                                : {}
                            }
                            columnData={item}
                            key={item.dataKey}
                            dataKey={item.dataKey}
                            width={0}
                            label={item.label}
                            cellRenderer={this.cellRenderer}
                            disableSort={!this.isSortEnabled(item)}
                            headerRenderer={this.headerRenderer}
                            flexGrow={item.flexGrow}
                          />
                        );
                      })}
                    </Table>
                    {isFunction(footerRenderer) ? (
                      <div
                        style={{
                          width: `${width}px`,
                          height: `${rowHeight}px`
                        }}
                        className={gatherClasses(
                          null,
                          "oui-react-virtualized-table-footer",
                          footerClassName
                        )}
                      >
                        {footerRenderer()}
                      </div>
                    ) : null}
                  </React.Fragment>
                )}
              </AutoSizer>
            )}
          </InfiniteLoader>
          <LoaderRow isLoading={isLoading} />
        </div>
      </div>
    );
  }
}

InfiniteTable.propTypes = {
  multiSelect: PropTypes.bool,
  noCheckboxTable: PropTypes.bool,
  dataList: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  selectAll: PropTypes.bool,
  columnsDef: PropTypes.arrayOf(PropTypes.object).isRequired,
  isRowDisabled: PropTypes.func,
  loadMoreRows: PropTypes.func,
  shouldClearLoadMap: PropTypes.func,
  getRowStyle: PropTypes.func,
  onRowClick: PropTypes.func,
  onSelectAll: PropTypes.func,
  sort: PropTypes.func,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.oneOf(["ASC", "DESC"]),
  threshold: PropTypes.number,
  minimumBatchSize: PropTypes.number,
  overscanRowCount: PropTypes.number,
  headerHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  remoteRowCount: PropTypes.number,
  uniqueDataKey: PropTypes.string,
  updateTooltip: PropTypes.func,
  hideTooltip: PropTypes.func,
  noDataMessage: PropTypes.oneOfType([
    PropTypes.string,
    (props, propName) => {
      const prop = props[propName];
      if (
        !(
          React.isValidElement(prop) &&
          prop.type.displayName === "OrionMessageBox"
        )
      ) {
        throw new Error(
          "noDataMessage must be either a string or a valid react element returned by <MessageBox />."
        );
      }
    }
  ]),
  isInvalid: PropTypes.bool,
  disabledRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  scrollToIndex: PropTypes.number,
  alignItems: PropTypes.oneOf(["top", "center", "bottom"]),
  // eslint-disable-next-line react/no-unused-prop-types
  textAlign: PropTypes.oneOf(["left", "center", "right"]),
  getSelectionInputTooltip: PropTypes.func,
  dataTestId: PropTypes.string,
  hideSelectAll: PropTypes.bool,
  isLoading: PropTypes.bool,
  tableId: PropTypes.string,
  footerClassName: PropTypes.string,
  footerRenderer: PropTypes.func
};

InfiniteTable.defaultProps = {
  selectedRows: [],
  selectAll: false,
  multiSelect: true,
  noCheckboxTable: false,
  isRowDisabled: () => {
    return false;
  },
  loadMoreRows: () => {},
  shouldClearLoadMap: () => {
    return false;
  },
  getRowStyle: () => {},
  onRowClick: () => {},
  onSelectAll: () => {},
  sort: () => {},
  updateTooltip: () => {},
  hideTooltip: () => {},
  sortBy: "",
  sortDirection: "DESC",
  threshold: 5,
  minimumBatchSize: 100,
  overscanRowCount: 5,
  headerHeight: 40,
  rowHeight: 40,
  remoteRowCount: 99999999999,
  uniqueDataKey: "id",
  noDataMessage: "No Data Found",
  isInvalid: false,
  scrollToIndex: 0,
  alignItems: "center",
  textAlign: "left",
  dataTestId: "orion-table",
  hideSelectAll: false,
  tableId: "orion-data-table",
  footerClassName: ""
};

InfiniteTable.displayName = "OrionInfiniteTable";

export default InfiniteTable;
